import React, { useState } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

import { Scatter } from "react-chartjs-2";

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

const bvimage = "/assets/biasvariance.png";

const bvLibrary = {
  Manufacturing: {
    Production: [
      "Production Output Variation",
      "Machine Setup Variation",
      "Production Cycle Time",
      "Batch Processing Time",
      "Assembly Line Balance",
    ],

    Quality: [
      "Quality Defects",
      "Inspection Failure Rate",
      "Product Return Rate",
      "Warranty Claim Rate",
    ],

    Procurement: [
      "Raw Material Quality Variation",
      "Supplier Delivery Delay",
      "Procurement Price Variation",
      "Vendor Reliability",
    ],

    Maintenance: [
      "Machine Downtime",
      "Preventive Maintenance Delay",
      "Tool Calibration Variation",
      "Machine Maintenance Compliance",
    ],

    Operations: [
      "Energy Consumption Variation",
      "Electricity Cost Fluctuation",
      "Water Usage Variation",
      "Packaging Cost Variation",
    ],
  },

  Retail: {
    Sales: [
      "Retail Pricing Changes",
      "Sales Conversion Rate",
      "Marketing Campaign Performance",
      "Lead Response Time",
    ],

    Inventory: [
      "Inventory Stockouts",
      "Warehouse Space Utilization",
      "Inventory Shrinkage",
      "Cold Storage Temperature Variation",
    ],

    Customer: [
      "Customer Complaints",
      "Customer Retention",
      "Brand Reputation Feedback",
      "Social Media Engagement",
    ],
  },

  Agriculture: {
    Crop: [
      "Crop Yield Variation",
      "Seed Quality Variation",
      "Fertilizer Application Variation",
      "Irrigation Timing Variation",
    ],

    Market: [
      "Market Demand Fluctuation",
      "Distributor Sales Variation",
      "Channel Inventory Variation",
    ],

    Risk: ["Weather Risk Impact", "Transport Cost Variation"],
  },

  Healthcare: {
    Operations: [
      "Clinic Patient Waiting Time",
      "Diagnosis Consistency",
      "Treatment Protocol Adherence",
    ],

    Pharmacy: ["Pharmacy Stock Availability", "Lab Test Turnaround Time"],
  },

  Education: {
    Students: [
      "Student Performance Variation",
      "Homework Completion Rates",
      "Course Completion Rate",
    ],

    Teaching: ["Teacher Evaluation Consistency", "Training Effectiveness"],
  },

  HORECA: {
    Kitchen: [
      "Restaurant Food Quality Variation",
      "Recipe Consistency",
      "Kitchen Preparation Time",
    ],

    Service: [
      "Service Waiting Time",
      "Table Turnover Rate",
      "Customer Feedback Variation",
    ],
  },
};

const sampleTrend = [
  { date: "Day 1", bias: 4, variance: 6 },
  { date: "Day 2", bias: 5, variance: 7 },
  { date: "Day 3", bias: 6, variance: 8 },
  { date: "Day 4", bias: 7, variance: 6 },
  { date: "Day 5", bias: 6, variance: 5 },
  { date: "Day 6", bias: 5, variance: 6 },
  { date: "Day 7", bias: 4, variance: 4 },
  { date: "Day 8", bias: 3, variance: 4 },
  { date: "Day 9", bias: 3, variance: 3 },
  { date: "Day 10", bias: 2, variance: 3 },
];

const BVMyHistory = () => {
  const [sector, setSector] = useState("");
  const [func, setFunc] = useState("");
  const [selectedCase, setSelectedCase] = useState("");
  const [showSample, setShowSample] = useState(false);

  const chartOptions = {
    scales: {
      x: {
        min: 0,
        max: 10,
        title: {
          display: true,
          text: "Variance",
          color: "white",
        },
        ticks: {
          color: "white",
        },
        grid: {
          color: "#444",
        },
      },

      y: {
        min: 0,
        max: 10,
        title: {
          display: true,
          text: "Bias",
          color: "white",
        },
        ticks: {
          color: "white",
        },
        grid: {
          color: "#444",
        },
      },
    },

    plugins: {
      legend: {
        labels: {
          color: "white",
        },
      },
    },
  };

  /* -------------------------------
Render
-------------------------------- */

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <h1 className="text-2xl font-bold text-yellow-200 mb-4">
        Bias-Variance History
      </h1>

      <p className="text-gray-300 mb-6">
        View historical operational stability trends of your MSME processes.
      </p>

      {/* Buttons */}

      <div className="flex gap-3 mb-6">
        <button
          disabled
          className="w-48 bg-gray-200 text-white px-4 py-2 rounded text-sm opacity-50 cursor-not-allowed"
        >
          My History
        </button>

        <button
          onClick={() => setShowSample(!showSample)}
          className="w-48 bg-blue-700 px-4 py-2 rounded text-sm"
        >
          Show Sample
        </button>
      </div>

      {/* Sector */}

      <h2 className="text-yellow-300 mb-2">Select Sector</h2>

      <select
        className="w-48 h-12 bg-gray-900 border border-gray-700 rounded p-2 mb-4"
        value={sector}
        onChange={(e) => {
          setSector(e.target.value);
          setFunc("");
          setSelectedCase("");
        }}
      >
        <option value="">Select Sector</option>

        {Object.keys(bvLibrary).map((s, i) => (
          <option key={i}>{s}</option>
        ))}
      </select>

      {/* Function */}

      {sector && (
        <>
          <h2 className="text-yellow-300 mb-2">Select Function</h2>

          <select
            className="w-56 h-12 bg-gray-900 border border-gray-700 rounded p-2 mb-4"
            value={func}
            onChange={(e) => {
              setFunc(e.target.value);
              setSelectedCase("");
            }}
          >
            <option value="">Select Function</option>

            {Object.keys(bvLibrary[sector]).map((f, i) => (
              <option key={i}>{f}</option>
            ))}
          </select>
        </>
      )}

      {/* Use Case */}

      {func && (
        <>
          <h2 className="text-yellow-300 mb-2">Select Use Case</h2>

          <select
            className="w-72 h-12 bg-gray-900 border border-gray-700 rounded p-2 mb-6"
            value={selectedCase}
            onChange={(e) => setSelectedCase(e.target.value)}
          >
            <option value="">Select Use Case</option>

            {bvLibrary[sector][func].map((u, i) => (
              <option key={i}>{u}</option>
            ))}
          </select>
        </>
      )}

      {/* Sample Trend Chart */}

      {showSample && selectedCase && (
        <>
        <div className="mt-6 bg-gray-900 border border-gray-700 rounded p-4">
          <h3 className="text-yellow-300 mb-3">
            Sample Historical Bias-Variance Trend
          </h3>

          <div className="bg-black p-3 rounded">
            <Scatter
              data={{
                datasets: [
                  {
                    label: "Historical Bias-Variance Trend",
                    data: sampleTrend.map((d) => ({
                      x: d.variance,
                      y: d.bias,
                    })),
                    backgroundColor: "yellow",
                    pointRadius: 7,
                  },
                ],
              }}
              options={chartOptions}
            />
          </div>

          {/* Timeline */}

          <div className="mt-4 text-xs text-gray-400 space-y-1">
            {sampleTrend.map((d, i) => (
              <div key={i}>
                {d.date} → Bias {d.bias} | Variance {d.variance}
              </div>
            ))}
          </div>

          <p className="text-sm text-gray-300 mt-4">
            This sample trend demonstrates how an MSME can monitor process stability over time using the Bias-Variance framework. As the
            process improves, bias and variance gradually reduce moving towards the stable zone.
          </p>
        </div>
        
        {/* Bias Variance Target Board */}
        <div className="flex flex-col items-center justify-center border border-cyan-200 mt-8 max-w-6xl p-3">
          <h2 className="text-yellow-300 mb-2">Bias-Variance Target Board</h2>
          <div className="flex-shrink-0 w-80 rounded overflow-hidden bg-gray-700 mb-4">
            <img
              src={bvimage}
              alt="Bias Variance"
              className="w-full object-cover"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 text-center text-xs lg:text-sm">
            <div className="bg-green-800 p-3 rounded">
              Low Bias
              <br />
              Low Variance
              <br />
              <span className="text-green-300">Excellent</span>
            </div>

            <div className="bg-blue-800 p-3 rounded">
              High Bias
              <br />
              Low Variance
              <br />
              <span className="text-blue-300">Rigid</span>
            </div>

            <div className="bg-yellow-700 p-3 rounded">
              Low Bias
              <br />
              High Variance
              <br />
              <span className="text-yellow-200">Chaotic</span>
            </div>

            <div className="bg-red-800 p-3 rounded">
              High Bias
              <br />
              High Variance
              <br />
              <span className="text-red-300">Danger</span>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default BVMyHistory;
